import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  Trash2,
  RefreshCw,
  Search,
  Mail,
  Phone,
  MessageCircle,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "fixtechnical_admin_token";

function LoginCard({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter the admin password");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Logged in");
      onLoggedIn(res.data.token);
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
      <div className="w-full max-w-md bg-white border border-neutral-200 p-8 lg:p-10 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 flex items-center justify-center bg-[#C5A059] text-white font-heading font-semibold">
            F+
          </div>
          <div>
            <p className="font-heading text-base font-medium leading-tight">
              Fix Technical Renovation Company
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
              Leads Admin
            </p>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-light text-neutral-900 mb-2">
          Admin Sign-in
        </h1>
        <p className="text-sm text-neutral-500 mb-6">
          Enter your admin password to view and manage renovation leads.
        </p>
        <form onSubmit={handleLogin} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="text-xs text-neutral-500 uppercase tracking-wider">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-password-input"
              className="rounded-none border-0 border-b border-neutral-200 px-0 focus-visible:ring-0 focus-visible:border-[#C5A059] h-11"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none py-6 font-medium"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
        <p className="text-[11px] text-neutral-400 text-center mt-5">
          <a href="/" className="hover:text-[#C5A059] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to landing page
          </a>
        </p>
      </div>
    </div>
  );
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

function exportToCsv(rows) {
  if (!rows.length) {
    toast.error("No leads to export");
    return;
  }
  const headers = ["Created At", "Name", "Phone", "Project Type", "Location", "Message", "Source"];
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      [
        formatDate(r.created_at),
        r.name,
        r.phone,
        r.project_type || "",
        r.location || "",
        (r.message || "").replace(/\n/g, " "),
        r.source || "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().split("T")[0];
  a.href = url;
  a.download = `fix-technical-renovation-leads-${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} leads`);
}

function AdminLeadsTable({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [nowMs] = useState(() => Date.now());
  const navigate = useNavigate();

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/leads`, { headers });
        if (!cancelled) setLeads(res.data || []);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          toast.error("Session expired. Please sign in again.");
          onLogout();
        } else {
          toast.error("Failed to load leads");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [headers, onLogout, reloadTick]);

  const fetchLeads = () => setReloadTick((t) => t + 1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.phone, l.project_type, l.location, l.message, l.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, search]);

  const stats = useMemo(() => {
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    return {
      total: leads.length,
      apartment: leads.filter((l) => (l.project_type || "").includes("Apartment")).length,
      villa: leads.filter((l) => (l.project_type || "").includes("Villa")).length,
      week: leads.filter((l) => nowMs - new Date(l.created_at).getTime() < weekMs).length,
    };
  }, [leads, nowMs]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/leads/${deleteTarget.id}`, { headers });
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error("Unable to delete lead");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="w-10 h-10 flex items-center justify-center bg-[#C5A059] text-white font-heading font-semibold hover:bg-[#A88746] transition-colors"
              data-testid="admin-home"
              aria-label="Back home"
            >
              F+
            </button>
            <div>
              <p className="font-heading text-base font-medium leading-tight">
                Renovation Leads Dashboard
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
                Fix Technical Renovation Company
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchLeads}
              variant="outline"
              className="rounded-none border-neutral-300 hover:border-[#C5A059] hover:text-[#C5A059]"
              data-testid="admin-refresh"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={() => exportToCsv(filtered)}
              className="bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none"
              data-testid="admin-export-csv"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="rounded-none border-neutral-300 hover:border-red-500 hover:text-red-500"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-8">
          <StatCard label="Total Leads" value={stats.total} testId="stat-total" />
          <StatCard label="Apartment" value={stats.apartment} testId="stat-apartment" />
          <StatCard label="Villa" value={stats.villa} testId="stat-villa" />
          <StatCard label="Last 7 days" value={stats.week} testId="stat-week" />
        </div>

        {/* Search */}
        <div className="bg-white border border-neutral-200 px-4 py-3 flex items-center gap-3 mb-4">
          <Search className="w-4 h-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, project type, location…"
            data-testid="admin-search"
            className="rounded-none border-0 px-0 focus-visible:ring-0 h-9"
          />
          <span className="text-xs text-neutral-500 whitespace-nowrap">
            {filtered.length} of {leads.length}
          </span>
        </div>

        {/* Table */}
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow>
                <TableHead className="font-heading">When</TableHead>
                <TableHead className="font-heading">Name</TableHead>
                <TableHead className="font-heading">Phone</TableHead>
                <TableHead className="font-heading">Project</TableHead>
                <TableHead className="font-heading">Location</TableHead>
                <TableHead className="font-heading">Source</TableHead>
                <TableHead className="font-heading">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-neutral-500">
                    Loading leads…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-neutral-500" data-testid="admin-empty">
                    {leads.length === 0 ? "No leads yet." : "No matching leads."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-lead-row-${l.id}`}>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {formatDate(l.created_at)}
                    </TableCell>
                    <TableCell className="font-medium text-neutral-900">{l.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <a
                          href={`tel:${l.phone}`}
                          className="text-neutral-700 hover:text-[#C5A059] inline-flex items-center gap-1"
                        >
                          <Phone className="w-3 h-3" /> {l.phone}
                        </a>
                        <a
                          href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#25D366] hover:text-[#1ebd5a]"
                          aria-label="WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{l.project_type || "—"}</TableCell>
                    <TableCell className="text-sm">{l.location || "—"}</TableCell>
                    <TableCell>
                      <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#F9F8F6] border border-neutral-200">
                        {l.source || "hero"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => setDeleteTarget(l)}
                        variant="ghost"
                        className="text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-none h-8 w-8 p-0"
                        data-testid={`admin-delete-${l.id}`}
                        aria-label="Delete lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail expand for messages */}
        {filtered.some((l) => l.message) && (
          <div className="mt-6 grid gap-3">
            {filtered
              .filter((l) => l.message)
              .slice(0, 10)
              .map((l) => (
                <div
                  key={`msg-${l.id}`}
                  className="bg-white border border-neutral-200 p-4 text-sm"
                >
                  <div className="flex items-center gap-3 mb-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#C5A059]" />
                    <span className="font-medium text-neutral-900">{l.name}</span>
                    <span className="text-xs text-neutral-400">
                      {formatDate(l.created_at)}
                    </span>
                  </div>
                  <p className="text-neutral-700 leading-relaxed">{l.message}</p>
                </div>
              ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {deleteTarget?.name}&apos;s enquiry. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="admin-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="admin-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-neutral-200 p-5 lg:p-6 hover:border-[#C5A059] transition-colors"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
        {label}
      </p>
      <p className="font-heading text-3xl font-light text-neutral-900">{value}</p>
    </div>
  );
}

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  if (!token) {
    return <LoginCard onLoggedIn={setToken} />;
  }

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return <AdminLeadsTable token={token} onLogout={handleLogout} />;
}
